import React, { useState } from 'react';
import { 
  Button, 
  TextField, 
  Typography, 
  Grid, 
  useMediaQuery, 
  useTheme, 
  InputAdornment
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function PasswordReset() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtém o email e código da página anterior
  const { email, code } = location.state || {};

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (!newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all password fields.');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/password/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          code, 
          newPassword, 
          confirmPassword 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Password reset successfully! Redirecting to login...');
        // Redireciona para o login após 2 segundos
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setErrorMessage(data.error || 'Error resetting password.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrorMessage('Error resetting password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      fontFamily: "'Poppins', sans-serif",
    },
     leftSection: {
      backgroundImage: "url('app2.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      color: 'rgb(0, 0, 0)',
      padding: '20px',
      minHeight: '100vh',
    },

    leftSectionFooter: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      backdropFilter: 'blur(5px)',
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      padding: '10px 20px',
      borderRadius: '10px',
      fontSize: '14px',
      width: '80%',
      maxWidth: '300px',
    },
    typographyH4: {
      fontWeight: 'bold',
      marginBottom: '10px',
      marginTop: '10px',
    },
    rightSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      width: '100%',
    },
    logo: {
      width: '80px',
      marginBottom: '20px',
    },
    formContainer: {
      width: '100%',
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    titleContainer: {
      textAlign: 'center',
      marginBottom: '20px',
      width: '100%',
    },
    button: {
      width: '100%',
      marginTop: '20px',
      backgroundColor: '#007bff',
      color: 'white',
      textTransform: 'none',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
      '&:hover': {
        backgroundColor: '#0056b3',
      },
    },
    backButton: {
      width: '100%',
      marginTop: '10px',
      textTransform: 'none',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
    },
    errorMessage: {
      marginTop: '10px',
      color: 'red',
      fontSize: '14px',
      textAlign: 'center',
    },
    successMessage: {
      marginTop: '10px',
      color: 'green',
      fontSize: '14px',
      textAlign: 'center',
    },
  };

  return (
    <Grid container style={styles.container}>
      {/* Seção da esquerda */}
      <Grid
        item
        xs={12}
        sm={6}
        style={styles.leftSection}
      >
         <Typography 
                  variant="h3" 
                  gutterBottom
                  style={{
                    color: '#000000',
                    textShadow: `
                      0 0 10px #fff,
                      0 0 10px #fff,
                      0 0 10px #fff,
                      0 0 10px #fff
                    `,
                    fontWeight: 'bold',
                    marginBottom: '10px'
                  }}
                >
                  Create a new password
                </Typography>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  style={{
                    color: '#000000',
                    textShadow: `
                      0 0 8px #fff,
                      0 0 8px #fff,
                      0 0 8px #fff
                    `,
                    fontWeight: '600',
                    marginBottom: '20px'
                  }}
                >
                 Choose a secure and strong password.
                </Typography>

        <div style={styles.leftSectionFooter}>
          <Typography variant="body2">
            Remember your password?{' '}
            <Link to="/" style={{ color: '#007bff', textDecoration: 'none' }}>
              Back to Login
            </Link>
          </Typography>
        </div>
      </Grid>

      {/* Seção da direita */}
      <Grid
        item
        xs={12}
        sm={6}
        style={styles.rightSection}
      >
        <div style={styles.formContainer}>
          {/* Container para título e logo CENTRALIZADO */}
          <div style={styles.titleContainer}>
            <img
              src="/logotipo.png"
              alt="Hub4Food Logo"
              style={{ ...styles.logo, width: isMobile ? '60px' : '80px' }}
            />
            <Typography variant="h5" gutterBottom>
              Set New Password
            </Typography>
          </div>

          <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
            <TextField
              label="New Password"
              type="password"
              variant="outlined"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              margin="normal"
              placeholder="Enter new password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockResetIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Must include uppercase, lowercase, number, and special character"
            />
            <TextField
              label="Confirm Password"
              type="password"
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              margin="normal"
              placeholder="Confirm new password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockResetIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              style={styles.button}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/password-recovery-code', { state: { email, code } })}
              style={styles.backButton}
            >
              Back to Code
            </Button>
          </form>

          {errorMessage && (
            <Typography style={styles.errorMessage}>
              {errorMessage}
            </Typography>
          )}

          {successMessage && (
            <Typography style={styles.successMessage}>
              {successMessage}
            </Typography>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

export default PasswordReset;